import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Text } from "@hangar/web-components/core/Text";
import * as S from './FlightChangeDetails.styles';
import { FlightChangeDetailsSkeleton } from './FlightChangeDetailsSkeleton';
import { DetailHeader } from '../../components/header/DetailHeader';
import { FlightChangeDetail, DetailHeaderViewModel, ItineraryComparisonViewModel, ReasonCode, isCaseReadOnlyStatus } from '../../services/api/flightChanges/types';
import { mapToDetailHeaderProps } from '../../services/api/flightChanges/mappers';
import { useFlightCasesService } from '../../hooks/flightCases/useFlightChangeCases';
import { useAssignment } from '../../hooks/flightCases/useAssignment';
import { ItineraryComparison } from '../../components/itinerary/ItineraryComparison';
import { mapToItineraryComparison } from '../../services/api/flightChanges/mappers';
import { ChangeCodeForm, ChangeCodeFormData, ACTION_TO_BACKEND_STATUS, CASE_STATUS_ACTIONS, SavedFormValues, CASE_STATUS_TO_BACKEND } from '../../components/ChangeCodeForm';
import { UpdateFlightCasePayload } from '../../services/api/flightChanges/types';
import { isNotFoundError, getErrorMessage } from '../../services/api/flightChanges/errors';
import { useTranslation } from '../../hooks/i18n';
import { ErrorMessage } from '../FlightChangesCases/ErrorMessage';
import {
  ManualReviewBanner,
  shouldShowManualReviewBanner,
} from '../../components/ManualReviewBanner';
import { useAuthStore } from '../../stores/authStore';
import { ROUTES, type FlightChangeDetailLocationState } from '../../router/routes';

const STORAGE_KEY_PREFIX = 'flightCase_savedForm_';

const getSavedFormFromStorage = (caseId: string): SavedFormValues | null => {
  try {
    const stored = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${caseId}`);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    if (!parsed.changeReason || typeof parsed.changeReason.isInitialReasonCodeCorrect !== 'boolean') {
      return null;
    }
    return parsed as SavedFormValues;
  } catch {
    return null;
  }
};

const saveFormToStorage = (caseId: string, values: SavedFormValues): void => {
  try {
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${caseId}`, JSON.stringify(values));
  } catch {
    console.error('Error saving form to storage:', values);
  }
};

export const FlightChangeDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const casesListReturnPath =
    (location.state as FlightChangeDetailLocationState | null)?.flightCasesListReturnTo ?? ROUTES.DASHBOARD;
  const [detailData, setDetailData] = useState<FlightChangeDetail | null>(null);
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveOnlySuccessMessage, setSuccessMessage] = useState<string | null>(null);
  const [localSavedValues, setLocalSavedValues] = useState<SavedFormValues | null>(null);
  const flightCasesService = useFlightCasesService();
  const { t } = useTranslation();
  const hasChangesRef = useRef<(() => boolean) | null>(null);
  const apiService = useAuthStore((state) => state.apiService);
  const isInitialized = useAuthStore((state) => state.isInitialized);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    
    if (!apiService || !isInitialized) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {      
      const response = await flightCasesService.getFlightCaseDetailById(id);
      setDetailData(response);
    } catch (err) {
      console.error('Error fetching flight case detail:', err);
      if (isNotFoundError(err)) {
          setError('NOT_FOUND');
      } else {
          setError(getErrorMessage(err, 'Error loading data'));
      }
  } finally {
      setIsLoading(false);
    }
  }, [id, flightCasesService, apiService, isInitialized]);

  const silentRefresh = useCallback(async () => {
    if (!id) return;
    
    try {      
      const response = await flightCasesService.getFlightCaseDetailById(id);
      setDetailData(response);
    } catch (err) {
      console.error('Error refreshing flight case detail:', err);
    }
  }, [id, flightCasesService]);

  const { handleAssign, conflictError, genericError, clearError, isAssigning } = useAssignment({
    onSuccess: fetchDetail,
    onError: silentRefresh,
  });

  useEffect(() => {
    if (id) {
      const storedValues = getSavedFormFromStorage(id);
      if (storedValues) {
        setLocalSavedValues(storedValues);
      }
    }
  }, [id]);

  const handleHasChangesRef = useCallback((hasChanges: () => boolean) => {
    hasChangesRef.current = hasChanges;
  }, []);

  const checkForUnsavedChanges = useCallback(() => {
    return hasChangesRef.current?.() ?? false;
  }, []);

  const fetchReasonCodes = useCallback(async () => {
    try {
      const response = await flightCasesService.getReasonCodes();
      setReasonCodes(response);
    } catch (err) {
      console.error('Error fetching reason codes:', err);
    }
  }, [flightCasesService]);

  useEffect(() => {
    if (apiService && isInitialized) {
      fetchDetail();
      fetchReasonCodes();
    }
  }, [fetchDetail, fetchReasonCodes, apiService, isInitialized]);

  const headerProps: DetailHeaderViewModel | null = useMemo(() => {
    if (!detailData) return null;
    return mapToDetailHeaderProps(detailData);
  }, [detailData]);

  const itineraryComparisonData: ItineraryComparisonViewModel | null = useMemo(() => {
    if (!detailData) return null;
    return mapToItineraryComparison(detailData, { normalizeToUtcWhenNoOffset: false });
  }, [detailData]);

  const changeReasonData = useMemo(() => {
    if (!detailData || reasonCodes.length === 0) {
      return {
        initialReasonCodeId: 0,
        reasonCode: '',
        remarkOptions: [],
        isInitialRemarkRequired: false
      };
    }

    const initialReasonCodeId = detailData.changeReason.initialReasonCodeId;
    const matchedReasonCode = initialReasonCodeId !== null 
      ? reasonCodes.find(rc => rc.id === initialReasonCodeId)
      : undefined;

    const reasonCodeDisplay = matchedReasonCode 
      ? `${matchedReasonCode.reasonCode} - ${matchedReasonCode.description}`
      : '';

    const remarkOptions = matchedReasonCode?.remarks ?? [];
    const isInitialRemarkRequired = matchedReasonCode?.isRemarkApplicable ?? false;

    return {
      initialReasonCodeId,
      reasonCode: reasonCodeDisplay,
      remarkOptions,
      isInitialRemarkRequired
    };
  }, [detailData, reasonCodes]);

  const savedFormValues: SavedFormValues | null = useMemo(() => {
    return localSavedValues || null;
  }, [localSavedValues]);

  const handleBackToList = useCallback(() => {
    navigate(casesListReturnPath);
  }, [navigate, casesListReturnPath]);

  const handleClose = () => {
    navigate(casesListReturnPath);
  };

  const handleSave = async (data: ChangeCodeFormData) => {
    if (!id) return;

    setSuccessMessage(null);

    const payload: UpdateFlightCasePayload = {
      notes: data.notes,
      changeReason: data.changeReason,
    };

    if (data.action === CASE_STATUS_ACTIONS.SAVE_ONLY && detailData?.status) {
      const currentStatus = detailData.status as string;
      payload.status = CASE_STATUS_TO_BACKEND[currentStatus] || currentStatus;
    } else {
      const backendStatus = ACTION_TO_BACKEND_STATUS[data.action];
      if (backendStatus) {
        payload.status = backendStatus;
      }
    }

    if (data.action === CASE_STATUS_ACTIONS.NOT_APPLICABLE && data.reasonNotApplicable) {
      payload.reasonNotApplicable = data.reasonNotApplicable;
    }

    try {
      const response = await flightCasesService.updateFlightCase(id, payload);

      const isKeepingSame = data.changeReason.isInitialReasonCodeCorrect;
      const savedValues: SavedFormValues = {
        changeReason: {
          isInitialReasonCodeCorrect: isKeepingSame,
          initialRemarkId: isKeepingSame ? (data.changeReason.initialRemarkId ?? null) : null,
          newReasonCodeId: isKeepingSame ? null : (data.changeReason.newReasonCodeId ?? null),
          newRemarkId: isKeepingSame ? null : (data.changeReason.newRemarkId ?? null),
        },
        notes: data.notes || '',
        reasonNotApplicable: data.reasonNotApplicable || '',
      };
      saveFormToStorage(id, savedValues);
      setLocalSavedValues(savedValues);

      if (data.action === CASE_STATUS_ACTIONS.SAVE_ONLY) {
        navigate(casesListReturnPath, { 
          state: { successMessage: t('flightChangeDetails.messages.changesSavedSuccessfully') } 
        });
        return;
      }
      
      if (data.action === CASE_STATUS_ACTIONS.ON_HOLD) {
        navigate(casesListReturnPath, { 
          state: { 
            successMessage: t('flightChangeDetails.messages.caseOnHoldSuccess'),
            successMessageBold: t('flightChangeDetails.messages.onHold')
          } 
        });
        return;
      }

      if (data.action === CASE_STATUS_ACTIONS.NOT_APPLICABLE) {
        navigate(casesListReturnPath, { 
          state: { 
            successMessage: t('flightChangeDetails.messages.caseNotApplicableSuccess'),
            successMessageBold: t('flightChangeDetails.messages.notApplicable')
          } 
        });
        return;
      }

      if (data.action === CASE_STATUS_ACTIONS.SEND_NOTIFICATION) {
        const responseText = typeof response === 'string' 
          ? response 
          : (response?.message || '');
        
        const responseTextLower = responseText.toLowerCase();
        
        const notificationWarnings = [
          {
            match: () => response?.missingContactInfo || 
              responseTextLower.includes('falta información de contacto') ||
              responseTextLower.includes('missing contact'),
            boldKey: 'missingContactInfoBold'
          },
          {
            match: () => response?.unsupportedCase ||
              responseTextLower.includes('no está soportado para el envío de notificaciones') ||
              responseTextLower.includes('not supported for sending notifications'),
            boldKey: 'unsupportedCaseBold'
          }
        ];
        
        const warning = notificationWarnings.find(w => w.match());
        
        if (warning) {
          navigate(casesListReturnPath, { 
            state: { 
              warningMessage: t('flightChangeDetails.messages.savedButWarning'),
              warningMessageBold: t(`flightChangeDetails.messages.${warning.boldKey}`)
            } 
          });
          return;
        }
        
        navigate(casesListReturnPath, { 
          state: { 
            successMessage: t('flightChangeDetails.messages.notificationSentSuccess'),
            successMessageBold: t('flightChangeDetails.messages.notificationSentSuccessBold')
          } 
        });
        return;
      }
      
      setSuccessMessage(t('flightChangeDetails.messages.changesSavedSuccessfully'));
      
      await fetchDetail();
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error saving flight case:', err);
      
      if (data.action === CASE_STATUS_ACTIONS.SEND_NOTIFICATION) {
        navigate(casesListReturnPath, { 
          state: { 
            errorMessage: t('flightChangeDetails.messages.notificationSentError'),
            errorMessageBold: t('flightChangeDetails.messages.notificationSentErrorBold')
          } 
        });
        throw err;
      }
      
      setError(getErrorMessage(err, 'Error saving data'));
      throw err;
    }
  };

  if (isLoading) {
    return <FlightChangeDetailsSkeleton />;
  }

  if (error === 'NOT_FOUND') {
    return (
        <S.Container>
            <ErrorMessage />
        </S.Container>
    );
  }

  if (error) {
    return <S.Container>Error: {error}</S.Container>;
  }

  if (!headerProps) {
    return <S.Container>No data available</S.Container>;
  }

  const assignmentErrorMessage = conflictError
    ? `${t("flightChangesCases.assignConflict.message")} ${conflictError.awb} ${t("flightChangesCases.assignConflict.messageSuffix")}`
    : genericError
    ? genericError.message
    : null;

  const showManualReviewBanner = shouldShowManualReviewBanner(
    detailData?.automationFeedback
  );

  return (
    <S.Container>
      <S.Content>
        <S.Header $hasManualReviewBanner={showManualReviewBanner}>
          <DetailHeader
            title={headerProps.title}
            branchNumber={headerProps.branchNumber}
            codes={headerProps.codes}
            route={headerProps.route}
            etd={headerProps.etd}
            pieces={headerProps.pieces}
            notificationStatus={headerProps.notificationStatus}
            caseStatus={headerProps.status}
            onBack={handleBackToList}
            hasUnsavedChanges={checkForUnsavedChanges}
            analyst={detailData?.analyst}
            isAssigning={isAssigning(Number(id))}
            onAssign={() => handleAssign(Number(id), detailData?.awb || '')}
            assignmentErrorMessage={assignmentErrorMessage}
            onClearAssignmentError={clearError}
          />
      </S.Header>
        <ManualReviewBanner automationFeedback={detailData?.automationFeedback} />
        {itineraryComparisonData ? (
            <ItineraryComparison data={itineraryComparisonData} />
        ) : (
            <S.ItineraryCard>
                <S.SectionTitleText>
                    <Text variant="heading-md">{t('itinerary.title')}</Text>
                </S.SectionTitleText>
                <div>{t('itinerary.empty')}</div>
            </S.ItineraryCard>
        )}
        <ChangeCodeForm
          changeReason={changeReasonData}
          reasonCodes={reasonCodes}
          contacts={detailData?.contacts ?? []}
          onClose={handleClose}
          onSave={handleSave}
          readOnly={!detailData?.analyst || isCaseReadOnlyStatus(detailData?.status)}
          saveOnlySuccessMessage={saveOnlySuccessMessage}
          onClearSuccessMessage={() => setSuccessMessage(null)}
          onHasChangesRef={handleHasChangesRef}
          savedValues={savedFormValues}
        />
      </S.Content>
    </S.Container>
  );
};
